import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { PoApprovalService } from '../../services/poApproval.service';
import { PoApprovalStatus } from '../../interfaces/procurement/poApproval.interface';
import { PurchaseOrder } from '../../interfaces/procurement/purchaseOrder.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { formatFc, isForeignCurrency } from '../../utils/fcDocuments'; // G5a polish

export const PendingApprovalPurchaseOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canApprove = usePermission(PERMISSIONS.PURCHASE_ORDER_APPROVE); // approve and reject are PURCHASE_ORDER_APPROVE server-side
  const toast = useToast();

  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [acting, setActing] = useState(false);
  const [approval, setApproval] = useState<PoApprovalStatus | null>(null); // B4-F

  useEffect(() => {
    if (!id) return;
    PurchaseOrderService.getById(id)
      .then(setPo)
      .catch(e => toast.showApiError(e, 'Failed to load purchase order'))
      .finally(() => setLoading(false));
    PoApprovalService.ofPurchaseOrder(id).then(setApproval).catch(() => setApproval(null));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async () => {
    if (!po) return;
    try {
      setActing(true);
      const updated = await PurchaseOrderService.approve(po.id);
      if (updated?.status === 'PENDING_APPROVAL') {
        // B4-F: a band that needs more approvals keeps the PO pending; show who is still needed.
        toast.showSuccess('Your approval is recorded; the purchase order is waiting for more approvals');
        setPo(updated);
        PoApprovalService.ofPurchaseOrder(po.id).then(setApproval).catch(() => setApproval(null));
        return;
      }
      toast.showSuccess('Purchase order approved');
      navigate('/procurement/purchase-orders/pending-approval');
    } catch (e) {
      toast.showApiError(e, 'Failed to approve purchase order');
    } finally {
      setActing(false);
      setApproveDialogOpen(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!po) return;
    try {
      setActing(true);
      setRejectDialogOpen(false);
      await PurchaseOrderService.reject(po.id);
      toast.showSuccess('Purchase order rejected');
      navigate('/procurement/purchase-orders/pending-approval');
    } catch (e) {
      toast.showApiError(e, 'Failed to reject purchase order');
    } finally {
      setActing(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!po) return <Typography sx={{ p: 3 }}>Purchase order not found</Typography>;
  const foreign = isForeignCurrency(po.currencyCode); // G5a polish: approvers see the PO currency, not only BDT
  const fcCode = po.currencyCode ?? 'BDT';

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.pendingPoDetail.page">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5">{po.poNumber}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip label={humanizeEnum(po.status)} color="warning" size="small" />
            <Typography variant="body2" color="text.secondary">Rev {po.revision}</Typography>
          </Box>
        </Box>
        {canApprove && (<Stack direction="row" spacing={2}>
          <Button
            data-help="procurement.pendingPoDetail.approve"
            variant="contained"
            color="success"
            onClick={() => setApproveDialogOpen(true)}
            disabled={acting || (approval !== null && !approval.callerCanApprove)}
          >
            Approve
          </Button>
          <Button
            data-help="procurement.pendingPoDetail.reject"
            variant="contained"
            color="error"
            onClick={() => setRejectDialogOpen(true)}
            disabled={acting}
          >
            Reject
          </Button>
        </Stack>)}
      </Box>

      {/* ---- B4-F: approval band and progress ---- */}
      {approval && approval.bandId && (
        <Paper sx={{ p: 2, mb: 2 }} data-testid="approval-progress" data-help-section="procurement.pendingPoDetail.progress">
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            Approval band {approval.bandLabel}: {approval.approvalsGiven} of {approval.approvalsRequired} approvals
          </Typography>
          <Typography variant="body2">Still needed: {approval.remaining}</Typography>
          <Typography variant="body2" color="text.secondary">Must include: {approval.requirement}</Typography>
          {approval.approvers.length > 0 && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Decided by: {approval.approvers.map(a => `${a.name ?? a.userId} (${a.decision.toLowerCase()})`).join(', ')}
            </Typography>
          )}
          {!approval.callerCanApprove && approval.callerBlockedReason && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>You cannot approve: {approval.callerBlockedReason}</Typography>
          )}
        </Paper>
      )}
      {/* ---- end B4-F ---- */}

      {/* Summary */}
      <Paper sx={{ p: 2, mb: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <Typography variant="body2" title={po.supplierId}>
          <strong>Supplier:</strong> {po.supplierName ?? po.supplierId}
        </Typography>
        <Typography variant="body2"><strong>Order Date:</strong> {formatDate(po.orderDate)}</Typography>
        <Typography variant="body2"><strong>Expected Date:</strong> {po.expectedDate ? formatDate(po.expectedDate) : '-'}</Typography>
        <Typography variant="body2"><strong>Tax Amount:</strong> {formatMoney(po.taxAmount)}</Typography>
        {foreign && (
          <Typography variant="body2" data-testid="po-approval-currency">
            <strong>Currency:</strong> {po.currencyCode} at the indicative rate {po.exchangeRate ?? '—'} BDT per {po.currencyCode}
          </Typography>
        )}
        <Typography variant="body2" sx={{ gridColumn: '1 / -1' }}>
          <strong>Notes:</strong> {po.notes ?? '-'}
        </Typography>
      </Paper>

      {/* Lines */}
      <TableContainer component={Paper} sx={{ mb: 2 }} data-help-section="procurement.pendingPoDetail.lines">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell data-help="procurement.poLine.col.item">Item</TableCell>
              <TableCell data-help="procurement.poLine.col.qty">Quantity</TableCell>
              <TableCell data-help="procurement.poLine.col.price">Unit Price{foreign ? ` (${fcCode})` : ''}</TableCell>
              <TableCell data-help="procurement.poLine.col.total">Line Total{foreign ? ` (${fcCode})` : ''}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(po.lines ?? []).map(l => (
              <TableRow key={l.id}>
                <TableCell>{l.itemName ? `${l.itemName}${l.itemCode ? ` (${l.itemCode})` : ''}` : '—'}</TableCell>
                <TableCell>{l.quantity}{l.unit ? ` ${l.unit}` : ''}</TableCell>
                <TableCell>{foreign ? formatFc(fcCode, l.foreignUnitPrice ?? null) : formatMoney(l.unitPrice)}</TableCell>
                <TableCell>{foreign ? formatFc(fcCode, l.foreignLineTotal ?? null) : formatMoney(l.lineTotal)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Totals */}
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
        {foreign ? (
          <>
            <Typography variant="h6">Total: {formatFc(fcCode, po.foreignTotal ?? null)}</Typography>
            <Typography variant="body2" color="text.secondary">≈ {formatMoney(po.totalAmount, 'BDT')} at the indicative rate {po.exchangeRate}</Typography>
          </>
        ) : (
          <>
            <Typography>Subtotal: {formatMoney(po.subtotal)}</Typography>
            <Typography>Tax: {formatMoney(po.taxAmount)}</Typography>
            <Typography variant="h6">Total: {formatMoney(po.totalAmount)}</Typography>
          </>
        )}
      </Paper>

      {/* Approve Dialog */}
      <ConfirmDialog
        open={approveDialogOpen}
        title="Approve this purchase order?"
        message="This will move the PO to the next stage of the procurement workflow."
        confirmLabel="Approve"
        confirmColor="success"
        onCancel={() => setApproveDialogOpen(false)}
        onConfirm={handleApprove}
        submitting={acting}
      />

      {/* Reject Dialog */}
      <Dialog data-help-page="procurement.pendingPoDetail.rejectDialog" open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Reject Purchase Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Reject this purchase order? It will be marked as rejected and cannot be reopened.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.pendingPoDetail.rejectCancel" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button data-help="procurement.pendingPoDetail.rejectConfirm" variant="contained" color="error" onClick={handleRejectConfirm}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingApprovalPurchaseOrderDetail;
